import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  ClipboardList,
  Target,
  Trophy,
  Users2,
  AlertOctagon,
  CreditCard,
  Sparkles,
  LogOut,
  Bell,
  Sparkle,
  Menu,
  X,
  Flag,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/daily-log", label: "Daily Log", icon: ClipboardList, testid: "nav-daily-log" },
  { to: "/scorecard", label: "Scorecard", icon: Trophy, testid: "nav-scorecard" },
  { to: "/goals", label: "Goals", icon: Flag, testid: "nav-goals" },
  { to: "/targets", label: "Targets", icon: Target, testid: "nav-targets", managerOnly: true },
  { to: "/dues", label: "Due Patients", icon: CreditCard, testid: "nav-dues" },
  { to: "/complaints", label: "Complaints", icon: AlertOctagon, testid: "nav-complaints" },
  { to: "/master-list", label: "Master List", icon: Users2, testid: "nav-master-list", managerOnly: true },
  { to: "/ai-assistant", label: "AI Assistant", icon: Sparkles, testid: "nav-ai", accent: true },
];

const Layout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [receptionists, setReceptionists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/receptionists").then((r) => setReceptionists(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
        <div className="text-[#C9A24B] animate-pulse font-heading text-xl">Loading…</div>
      </div>
    );
  }

  const isManager = user.role === "manager" || user.role === "super_admin";
  const filteredNav = NAV.filter((n) => !n.managerOnly || isManager);

  const notifCount =
    (summary?.complaint_funnel?.overdue_48h || 0) +
    (summary?.dues?.overdue_30 || 0) +
    (summary?.dues?.overdue_15 || 0);

  return (
    <div className="min-h-screen flex bg-[#0F172A] text-white grain">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`w-64 bg-[#0B1120] border-r border-[#334155] flex flex-col shrink-0 z-40
          fixed inset-y-0 left-0 transform transition-transform duration-300 lg:relative lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="p-6 border-b border-[#334155]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#C9A24B] flex items-center justify-center gold-glow">
              <Sparkle size={20} className="text-[#0F172A]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading text-white text-lg leading-tight">Youngness</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#C9A24B]">Reception CRM</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    isActive
                      ? "bg-[#1E293B] text-white border border-[#334155]"
                      : "text-slate-400 hover:text-white hover:bg-[#1E293B]/50"
                  } ${item.accent ? "text-[#C9A24B] hover:text-[#D4AF60]" : ""}`
                }
              >
                <Icon size={18} />
                <span className="font-body">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-[#334155]">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
            Signed in as
          </div>
          <div className="text-sm text-slate-300 truncate">{user.email}</div>
          <div className="mt-2">
            <Badge className="bg-[#1E293B] border border-[#334155] text-[#C9A24B] hover:bg-[#1E293B]">
              {user.role === "super_admin" ? "Super Admin" : user.role === "manager" ? "Manager" : "Receptionist"}
            </Badge>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-[#334155] bg-[#0F172A] flex items-center justify-between px-4 md:px-6 lg:px-8 shrink-0 relative z-10 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="sidebar-toggle"
              onClick={() => setSidebarOpen((s) => !s)}
              className="lg:hidden w-10 h-10 rounded-lg bg-[#1E293B] border border-[#334155] flex items-center justify-center text-slate-300 shrink-0"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="min-w-0">
              <div className="overline">Reception Operations</div>
              <div className="font-heading text-white text-base md:text-lg leading-none mt-1 truncate">
                New Delhi · Mathura · Jaipur
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right">
              <div className="overline">Active Alerts</div>
              <div className="text-slate-300 text-sm">
                {summary ? `${notifCount} pending` : "…"}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="notification-bell"
                  className="relative w-10 h-10 rounded-lg bg-[#1E293B] border border-[#334155] flex items-center justify-center hover:border-[#C9A24B] transition-colors"
                >
                  <Bell size={18} className="text-slate-300" />
                  {notifCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
                      {notifCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-[#1E293B] border-[#334155] text-slate-200">
                <DropdownMenuLabel className="font-heading text-white">Pending Alerts</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#334155]" />
                {summary?.complaint_funnel?.overdue_48h > 0 && (
                  <DropdownMenuItem className="focus:bg-[#0F172A]">
                    <AlertOctagon size={16} className="text-[#DC2626] mr-2" />
                    <span className="text-sm">
                      {summary.complaint_funnel.overdue_48h} complaint(s) open for 48+ hours
                    </span>
                  </DropdownMenuItem>
                )}
                {summary?.dues?.overdue_30 > 0 && (
                  <DropdownMenuItem className="focus:bg-[#0F172A]">
                    <CreditCard size={16} className="text-[#DC2626] mr-2" />
                    <span className="text-sm">{summary.dues.overdue_30} due(s) overdue 30+ days</span>
                  </DropdownMenuItem>
                )}
                {summary?.dues?.overdue_15 > 0 && (
                  <DropdownMenuItem className="focus:bg-[#0F172A]">
                    <CreditCard size={16} className="text-amber-400 mr-2" />
                    <span className="text-sm">{summary.dues.overdue_15} due(s) overdue 15+ days</span>
                  </DropdownMenuItem>
                )}
                {notifCount === 0 && (
                  <DropdownMenuItem disabled className="text-slate-500 focus:bg-transparent">
                    No pending alerts
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="flex items-center gap-3">
                  <Avatar className="w-9 h-9 border border-[#334155]">
                    <AvatarImage src={user.picture || undefined} />
                    <AvatarFallback className="bg-[#1E293B] text-[#C9A24B] font-mono text-sm">
                      {(user.name || user.email).slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1E293B] border-[#334155] text-slate-200 w-56">
                <DropdownMenuLabel className="font-heading">{user.name}</DropdownMenuLabel>
                <DropdownMenuItem disabled className="text-xs text-slate-500">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#334155]" />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="logout-btn"
                  className="focus:bg-[#0F172A] cursor-pointer"
                >
                  <LogOut size={16} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 md:p-6 lg:p-8 relative z-10">
          <Outlet context={{ user, summary, receptionists, branches }} />
        </main>
      </div>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
};

export default Layout;
